import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import getSpread from 'editor/src/store/design/selector/getSpread';
import getSpreadGroups from 'editor/src/store/design/selector/getSpreadGroups';
import getSpreadPrintAreaSize from 'editor/src/store/design/selector/getSpreadPrintAreaSize';
import { MediaElement } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getSettingsValue from 'editor/src/store/editor/selector/getSettingsValue';
import { SettingsProperty } from 'editor/src/store/editor/types';
import { useSelector } from 'editor/src/store/hooks';

import useSpreadTitle from 'editor/src/util/design/useSpreadTitle';
import findMultiTechAreaGroup from 'editor/src/util/findMultiTechAreaGroup';
import getSpreadGroup from 'editor/src/util/getSpreadGroup';
import { formatSize } from 'editor/src/util/math';

import { currentOperationManager } from 'editor/src/component/EditorArea/ElementOperationOverlay';

import styles from './index.module.scss';

function SizeReadout() {
  const { t } = useTranslation();
  const { spreadIndex, spread, spreadGroups, areaGroups, unit, selectedElement } = useSelector((state) => {
    const index = getCurrentSpreadIndex(state);
    const selected = getSelectedMediaElementsObjects(state);
    return {
      spreadIndex: index,
      spread: getSpread(state, index),
      spreadGroups: getSpreadGroups(state),
      areaGroups: state.app.multiTechAreaGroups,
      unit: getSettingsValue(state, SettingsProperty.units),
      selectedElement: selected.length === 1 ? selected[0] : undefined,
    };
  }, shallowEqual);
  const areaSize = useMemo(() => getSpreadPrintAreaSize(spread), [spread]);
  // Same label as the page strip: a multi-tech spread's own name carries its size and technology.
  const areaGroup = useMemo(() => findMultiTechAreaGroup(areaGroups, spread?.name), [areaGroups, spread?.name]);
  const spreadTitle = useSpreadTitle(
    getSpreadGroup(spreadIndex, spreadGroups)?.name ?? spread?.name ?? '',
    spreadIndex,
    undefined,
  );
  const areaName = areaGroup?.displayName ?? spreadTitle;

  // The store only learns the new size on release; follow the drag so the numbers move with the handles.
  const [draggedElement, setDraggedElement] = useState<MediaElement>();
  useEffect(() => {
    const onUpdating = (element: MediaElement) => setDraggedElement(element);
    const onStop = () => setDraggedElement(undefined);
    currentOperationManager.on('objectUpdating', onUpdating);
    currentOperationManager.on('objectUpdateStop', onStop);
    return () => {
      currentOperationManager.off('objectUpdating', onUpdating);
      currentOperationManager.off('objectUpdateStop', onStop);
    };
  }, []);

  const current = draggedElement ?? selectedElement;
  const element = current && current.type !== 'line' ? current : undefined;
  if (!areaSize && !element) {
    return null;
  }

  const unitLabel = t(`editor-${unit}`);

  return (
    <div className={styles.SizeReadout} data-testid="size-readout">
      {areaSize && (
        <div data-testid="size-readout-area">
          {t('editor-size-area', {
            name: areaName,
            width: formatSize(areaSize.width, unit),
            height: formatSize(areaSize.height, unit),
            unit: unitLabel,
          })}
        </div>
      )}
      {element && (
        <div data-testid="size-readout-element">
          {t('editor-size-element', {
            width: formatSize(element.width, unit),
            height: formatSize(element.height, unit),
            unit: unitLabel,
          })}
        </div>
      )}
    </div>
  );
}

export default React.memo(SizeReadout);
